import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useState } from 'react'
import { TriangleAlert, Users } from 'lucide-react'
import { Badge } from '@/components/kit/badge'
import { Button } from '@/components/kit/button'
import { Plate } from '@/components/kit/plate'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { EmptyState } from '@/components/kit/empty-state'
import { StaffChoice } from '@/components/staff/staff-choice'
import { useNow } from '@/hooks/use-now'
import { useCarWash } from '@/hooks/use-carwash'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { buildStaffBoard } from '@/lib/carwash/selectors'
import { plateLabel } from '@/lib/carwash/plate'

interface StaffPickerSheetProps {
    jobId: string | null
    open: boolean
    onClose: () => void
}

/* Assign a waiting vehicle to staff, or hand a vehicle over to someone else. */
export const StaffPickerSheet: FC<StaffPickerSheetProps> = ({ jobId, open, onClose }): ReactNode => {
    const { state, lookups } = useCarWash()
    const actions = useCarWashActions()
    const toast = useToast()
    const now = useNow(15_000)

    const job = jobId && lookups ? lookups.jobs.get(jobId) ?? null : null
    const reassign = Boolean(job && job.status !== 'waiting')

    const board = useMemo(() => state && now ? buildStaffBoard(state, now) : [], [state, now])
    const candidates = board.filter(entry => entry.status !== 'inactive' && entry.staff.id !== job?.staffId)
    const available = candidates.filter(entry => entry.status === 'available')
    const busy = candidates.filter(entry => entry.status === 'busy')
    const onBreak = candidates.filter(entry => entry.status === 'break')
    const maxToday = Math.max(1, ...board.map(entry => entry.completedToday))

    const [selectedId, setSelectedId] = useState<string | null>(null)

    const suggestedId = available[0]?.staff.id ?? null

    // Pick the suggested staff member only when the sheet opens for a vehicle,
    // so a later change in availability never overrides the operator's choice.
    useEffect(() => {
        if (open) setSelectedId(suggestedId)
    }, [open, jobId]) // eslint-disable-line react-hooks/exhaustive-deps

    const selected = candidates.find(entry => entry.staff.id === selectedId) ?? null
    const currentStaff = job?.staffId ? lookups?.staff.get(job.staffId) ?? null : null
    const plate = job ? plateLabel(job.plate, `Vehicle #${job.number}`) : ''

    const submit = (start: boolean) => {
        if (!job || !selected) return

        const member = selected.staff

        if (selected.status === 'break' && !actions.setStaffDuty(member.id, 'on_duty')) return
        if (!actions.assignStaff(job.id, member.id, { start, allowBusyStaff: selected.status === 'busy' })) return

        if (reassign) toast({ title: `${member.name} has taken over ${plate}`, description: currentStaff ? `${currentStaff.name} is free for the next vehicle.` : undefined })
        else if (start) toast({ title: `Washing started for ${plate}`, description: `${member.name} is on it. The timer is running.` })
        else toast({ title: `${plate} assigned to ${member.name}`, description: 'Tap Start wash when they begin.' })

        onClose()
    }

    const warning = selected?.status === 'busy'
        ? `${selected.staff.name} is already busy. They will have two vehicles at once.`
        : selected?.status === 'break'
            ? `${selected.staff.name} is on a break. Assigning ends the break.`
            : null

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title={reassign ? 'Change staff' : 'Assign staff'}
            description={job ? `${job.serviceName}${currentStaff ? `, now with ${currentStaff.name}` : ''}` : undefined}
            footer={job && (
                <div className='space-y-3'>
                    {warning && (
                        <p className='flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] font-medium text-amber-900'>
                            <TriangleAlert className='mt-px size-4 shrink-0' aria-hidden='true' />
                            {warning}
                        </p>
                    )}

                    {reassign
                        ? (
                            <Button size='lg' className='w-full' disabled={!selected} onClick={() => submit(false)}>
                                {selected ? `Hand over to ${selected.staff.name}` : 'Choose a staff member'}
                            </Button>
                        )
                        : (
                            <div className='grid grid-cols-[auto_1fr] gap-3'>
                                <Button variant='secondary' size='lg' disabled={!selected} onClick={() => submit(false)}>
                                    Assign only
                                </Button>

                                <Button size='lg' disabled={!selected} onClick={() => submit(true)}>
                                    {selected?.status === 'busy' ? 'Assign anyway and start' : 'Assign and start wash'}
                                </Button>
                            </div>
                        )}
                </div>
            )}
        >
            {job && (
                <div className='mb-4 flex items-center justify-between gap-3 rounded-xl bg-surface px-3 py-2.5'>
                    <Plate value={job.plate} size='md' />
                    <span className='truncate text-[13px] font-medium text-muted-foreground'>
                        {job.vehicleModel ?? 'Model not added'}
                    </span>
                </div>
            )}

            {!candidates.length && (
                <EmptyState
                    icon={<Users />}
                    title='No other staff on duty'
                    description='Add staff or set someone back to available from the Staff page.'
                />
            )}

            <div role='radiogroup' aria-label='Staff' className='space-y-5'>
                {available.length > 0 && (
                    <section>
                        <h3 className='mb-2 text-xs font-bold tracking-wide text-emerald-700 uppercase'>
                            {`Available (${available.length})`}
                        </h3>

                        <div className='space-y-2'>
                            {available.map((entry, index) => (
                                <StaffChoice
                                    key={entry.staff.id}
                                    entry={entry}
                                    now={now}
                                    maxToday={maxToday}
                                    selected={entry.staff.id === selectedId}
                                    onSelect={() => setSelectedId(entry.staff.id)}
                                    tag={index === 0 && available.length > 1 ? <Badge tone='blue'>Next in line</Badge> : undefined}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {busy.length > 0 && (
                    <section>
                        <h3 className='mb-2 text-xs font-bold tracking-wide text-sky-800 uppercase'>
                            {`Busy (${busy.length})`}
                        </h3>

                        <div className='space-y-2'>
                            {busy.map(entry => (
                                <StaffChoice
                                    key={entry.staff.id}
                                    entry={entry}
                                    now={now}
                                    maxToday={maxToday}
                                    selected={entry.staff.id === selectedId}
                                    onSelect={() => setSelectedId(entry.staff.id)}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {onBreak.length > 0 && (
                    <section>
                        <h3 className='mb-2 text-xs font-bold tracking-wide text-amber-800 uppercase'>
                            {`On break (${onBreak.length})`}
                        </h3>

                        <div className='space-y-2'>
                            {onBreak.map(entry => (
                                <StaffChoice
                                    key={entry.staff.id}
                                    entry={entry}
                                    now={now}
                                    maxToday={maxToday}
                                    selected={entry.staff.id === selectedId}
                                    onSelect={() => setSelectedId(entry.staff.id)}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </Sheet>
    )
}
